import { z } from "zod";
import { Parser } from "expr-eval";
import { optionalUnlessRequired, type FieldDescriptor } from "./types.js";

/**
 * Read-only, computed from other fields with a **safe** expression evaluator.
 *
 * CLAUDE.md and docs/01-PRODUCT-SPEC.md §3.2 are explicit: never `eval`, never `new Function`.
 * `expr-eval` parses to its own AST and evaluates against a supplied variable scope, so a form
 * author — who is staff, but not necessarily trusted with arbitrary code execution on the API
 * process — cannot reach the host environment from a formula.
 */
const configSchema = z.object({
  // e.g. "cgpa * 10" or "(score_a + score_b) / 2"
  expression: z.string().min(1).max(500).default("0"),
  precision: z.number().int().min(0).max(6).default(2),
  // Field keys this formula reads. Kept explicit rather than inferred so draft validation can
  // report a formula referencing a deleted field without having to parse every expression itself.
  dependsOn: z.array(z.string()).default([]),
});

type FormulaConfig = z.infer<typeof configSchema>;

/**
 * Shared parser instance. Operator support is deliberately narrowed: assignment would let an
 * expression mutate the scope it was handed, and the `f(x)=...` function-definition syntax turns a
 * formula field into a small programming language. Neither is needed to average two scores.
 */
const parser = new Parser({
  operators: {
    add: true,
    subtract: true,
    multiply: true,
    divide: true,
    remainder: true,
    power: true,
    comparison: true,
    logical: true,
    conditional: true,
    assignment: false,
    concatenate: false,
    in: false,
  },
});

export interface FormulaEvaluation {
  ok: boolean;
  value?: number;
  error?: string;
}

/**
 * Evaluates a formula against the current answers. Returns a result object rather than throwing:
 * a formula that cannot be computed yet (because its inputs are blank) is the normal state of a
 * half-filled form, not an error worth interrupting the applicant over.
 */
export const evaluateFormula = (
  expression: string,
  scope: Record<string, unknown>,
  precision = 2,
): FormulaEvaluation => {
  try {
    const parsed = parser.parse(expression);

    // Only finite numbers enter the scope. A blank answer would otherwise coerce to 0 and produce
    // a confidently wrong number — worse than reporting that the formula is not ready.
    const numericScope: Record<string, number> = {};
    for (const key of parsed.variables()) {
      const raw = scope[key];
      const asNumber = typeof raw === "number" ? raw : Number(raw);
      if (raw === undefined || raw === null || raw === "" || !Number.isFinite(asNumber)) {
        return { ok: false, error: `Waiting for a value for "${key}"` };
      }
      numericScope[key] = asNumber;
    }

    const result: unknown = parsed.evaluate(numericScope);
    if (typeof result !== "number" || !Number.isFinite(result)) {
      return { ok: false, error: "The formula did not produce a number" };
    }

    return { ok: true, value: Number(result.toFixed(precision)) };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : "Invalid formula" };
  }
};

/** Parses without evaluating — used by draft validation to reject a malformed expression early. */
export const parseFormula = (
  expression: string,
): { ok: boolean; variables: string[]; error?: string } => {
  try {
    const parsed = parser.parse(expression);
    return { ok: true, variables: parsed.variables() };
  } catch (error) {
    return {
      ok: false,
      variables: [],
      error: error instanceof Error ? error.message : "Invalid formula",
    };
  }
};

export const formulaField: FieldDescriptor<FormulaConfig> = {
  type: "formula",
  label: "Formula",
  category: "number",
  icon: "Sigma",
  configSchema,
  // The server recomputes formulas from their inputs at submit; a client-supplied value is never
  // trusted. Accepting a number here keeps the round-trip lossless for display purposes.
  valueSchema: (_config, isRequired) =>
    optionalUnlessRequired(
      z.number({ invalid_type_error: "Computed value must be a number" }),
      isRequired,
    ),
  defaultConfig: { expression: "0", precision: 2, dependsOn: [] },
  supportsProfileBinding: false,
  defaultSearchable: false,
  sortKind: "numeric",
  renderHints: { isReadOnly: true },
};
